import React from "react";
//#region types
import { EditableFormInnerProps, GenericFormSection, FieldGroupSectionProps } from "models/propTypes/genericFormProps";
//#endregion
//#region components
import { Divider, Grid } from "@mui/material";
import useCheckVisibility from "./hooks/useCheckVisibility";
import GenericFormInner from "./genericFormInner";
import Icon from "../icon";
import SimpleExpansionPanel from "../panels/simpleExpansionPanel";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { FormHelper, LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import clsx from "clsx";

function EditableFormInnerFieldGroupSection<T extends Record<string, any>>(props: EditableFormInnerProps<T>) {
  const { getFieldHidden } = useCheckVisibility<T>();
  const sections = props.fields as GenericFormSection<T>[];

  const getSectionNumber = (index: number) => {
    if (!props.parentSectionNumber) return index.toString();

    return `${props.parentSectionNumber}.${
      props.parentSectionNumber.indexOf(".") !== -1 ? index : String.fromCharCode(96 + index)
    }`;
  };

  let currentSectionIndex = 0;

  return (
    <Grid container direction="column">
      {sections.map((section, index) => {
        if (getFieldHidden(section.hidden)) return <React.Fragment key={`fieldGroupSection-${index}`} />;

        let sectionNumber = props.parentSectionNumber;
        if (!UtilsHelper.isEmpty(section.label)) {
          sectionNumber = getSectionNumber(++currentSectionIndex);
        }

        const extraProps = (section.extraProps ?? {}) as FieldGroupSectionProps;
        const innerFields = (
          <GenericFormInner<T>
            {...props}
            key={`fieldGroupSectionInner-${index}`}
            fields={section.innerFields}
            inline={section.inline ?? props.inline}
            enableControlLables={section.enableControlLables ?? props.enableControlLables}
            formGridLayout={section.formGridLayout ?? props.formGridLayout}
            parentSectionNumber={sectionNumber}
          />
        );

        return (
          <Grid
            key={`fieldGroupSection-${index}`}
            item
            container
            className={clsx(cssExports.fieldGroupSection, section.className)}
            id={FormHelper.getSectionNavKey(section)}
          >
            {!extraProps.isExpandable ? (
              <>
                {(section.label || section.descriptionText) && (
                  <Grid item container className={cssExports.formSectionHeader} alignItems="center" spacing={2}>
                    {section.label && (
                      <>
                        <Grid item container direction="row">
                          {section.icon && (
                            <Grid item>
                              <Icon icon={section.icon} />
                            </Grid>
                          )}
                          <Grid item>
                            {props.enableSectionNumbering && (
                              <span className={cssExports.formSectionNumber}>{sectionNumber}</span>
                            )}
                            {LocalizationHelperInstance.getFormattedMessage(section.label)}
                          </Grid>
                        </Grid>
                        <Grid item container className={cssExports.border}>
                          <Divider className={cssExports.dividerGray} />
                        </Grid>
                      </>
                    )}

                    {section.descriptionText && (
                      <Grid item container className={cssExports.formControlDescription} alignItems="center">
                        {typeof section.descriptionText === "string"
                          ? LocalizationHelperInstance.getFormattedMessage(section.descriptionText)
                          : section.descriptionText}
                      </Grid>
                    )}
                  </Grid>
                )}
                {innerFields}
              </>
            ) : (
              <SimpleExpansionPanel
                title={section.label ? LocalizationHelperInstance.getFormattedMessage(section.label) : ""}
                summary={
                  section.descriptionText &&
                  (typeof section.descriptionText === "string"
                    ? LocalizationHelperInstance.getFormattedMessage(section.descriptionText)
                    : section.descriptionText)
                }
                borderless
              >
                {innerFields}
              </SimpleExpansionPanel>
            )}
          </Grid>
        );
      })}
    </Grid>
  );
}

export default EditableFormInnerFieldGroupSection;
